import type { ReactNode } from 'react'
import { AlertCircle, Loader2, RefreshCw, SearchX } from 'lucide-react'
import { ApiError, SessionExpiredError } from '../lib/api'
import { Card } from './ui'

export function LoadingBlock({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted">
      <Loader2 size={16} className="animate-spin" />
      {label}
    </div>
  )
}

/** Skeleton rows for lists that have a known shape, so the page does not jump. */
export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2.5">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="h-14 animate-pulse rounded-2xl bg-surface" />
      ))}
    </div>
  )
}

/**
 * A 404 from any school route means "does not exist **or** is not yours", and the
 * two are indistinguishable on purpose — confirming which ids exist inside other
 * schools is exactly what that rule prevents. So it is always rendered as
 * "not found", never as "no permission".
 */
export function ErrorBlock({
  error,
  onRetry,
  notFoundLabel = 'Not found.',
}: {
  error: Error
  onRetry?: () => void
  notFoundLabel?: string
}) {
  if (error instanceof SessionExpiredError) {
    return <Message icon={<AlertCircle size={22} />} tone="rose" title="Session expired" body="Sign in again to continue." />
  }

  if (error instanceof ApiError && error.isNotFound) {
    return <Message icon={<SearchX size={22} />} tone="slate" title={notFoundLabel} body="It may have been archived, or it isn't part of your school." />
  }

  if (error instanceof ApiError && error.isForbidden) {
    return <Message icon={<AlertCircle size={22} />} tone="amber" title="Admins only" body="Your account doesn't have permission for this page." />
  }

  return (
    <Message
      icon={<AlertCircle size={22} />}
      tone="rose"
      title="Something went wrong"
      body={error.message}
      action={
        onRetry && (
          <button
            onClick={onRetry}
            className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-surface px-4 py-2 text-sm font-semibold text-ink shadow-sm transition hover:bg-surface"
          >
            <RefreshCw size={14} /> Try again
          </button>
        )
      }
    />
  )
}

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon: ReactNode
  title: string
  body: string
  action?: ReactNode
}) {
  return <Message icon={icon} tone="slate" title={title} body={body} action={action} />
}

const tones = {
  rose: 'bg-danger-wash text-danger',
  amber: 'bg-warn-wash text-warn',
  slate: 'bg-raised text-muted',
} as const

function Message({
  icon,
  tone,
  title,
  body,
  action,
}: {
  icon: ReactNode
  tone: keyof typeof tones
  title: string
  body: string
  action?: ReactNode
}) {
  return (
    <Card className="flex flex-col items-center py-12 text-center">
      <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ${tones[tone]}`}>
        {icon}
      </div>
      <p className="text-base font-semibold tracking-tight text-ink">{title}</p>
      <p className="mt-1 max-w-sm text-sm text-muted">{body}</p>
      {action}
    </Card>
  )
}

/** Toolbar refresh. Explicit, because nothing in this app polls. */
export function RefreshButton({ onClick, busy }: { onClick: () => void; busy?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-2 rounded-2xl bg-surface px-3.5 py-2 text-sm font-semibold text-muted shadow-sm transition hover:bg-surface disabled:opacity-60"
    >
      <RefreshCw size={14} className={busy ? 'animate-spin' : undefined} />
      Refresh
    </button>
  )
}
